import type { IResponse, IApiResponse, IListApiResponse } from "../@types/ApiResponse";
import { emptySplitApi } from "./emptySliceApi";
import endpoints from "./endpoints";

export const componentContentApi = emptySplitApi.injectEndpoints({
    endpoints: (builder) => ({
        // Create component content
        CreateComponentContent: builder.mutation<
            IResponse<ComponentContent.ComponentContentDto>,
            ComponentContent.ComponentContentDto
        >({
            query: (data) => ({
                url: endpoints.componentContent,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ComponentContent'],
        }),

        // Get component content by ID
        GetComponentContentById: builder.query<
            IResponse<ComponentContent.ComponentContentList>,
            string | number
        >({
            query: (id) => ({
                url: `${endpoints.componentContent}/${id}`,
                method: 'GET',
            }),
            providesTags: ["ComponentContent"],
        }),

        // Get component content by component ID
        GetComponentContentByComponentId: builder.query<
            IResponse<ComponentContent.ComponentContentList[]>,
            number
        >({
            query: (componentId) => ({
                url: `${endpoints.componentContent}/component/${componentId}`,
                method: 'GET',
            }),
            providesTags: ["ComponentContent"],
        }),

        // Update component content
        UpdateComponentContent: builder.mutation<
            IResponse<ComponentContent.ComponentContentDto>,
            { id: string | number; data: Partial<ComponentContent.ComponentContentDto> }
        >({
            query: ({ id, data }) => ({
                url: `${endpoints.componentContent}/${id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ["ComponentContent"],
        }),

        // Delete component content
        DeleteComponentContent: builder.mutation<
            IResponse<void>,
            string | number
        >({
            query: (id) => ({
                url: `${endpoints.componentContent}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ["ComponentContent"],
        }),

        // Get all component contents with filter
        GetComponentContents: builder.query<
            IResponse<ComponentContent.ComponentContentList[]>,
            { componentId?: number; screenFrameConfigId?: number; }
        >({
            query: ({ componentId, screenFrameConfigId }) => ({
                url: `${endpoints.componentContent}?componentId=${componentId}&screenFrameConfigId=${screenFrameConfigId}`,
                method: 'GET',
            }),
            providesTags: ["ComponentContent"],
        }),
        ComponentContentOrder: builder.mutation<
            IResponse<any>,
            ComponentContent.ComponentContentOrder
        >({
            query: (data) => ({
                url: endpoints.componentContents.order,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ComponentContent'],
        }),
        // Get component content history (paginated + filterable)
        GetComponentContentHistory: builder.query<
            IListApiResponse<ComponentContent.ComponentContentHistoryDto>,
            {
                componentId?: number;
            }
        >({
            query: (params) => {
                const queryParams = new URLSearchParams();
                if (params.componentId !== undefined) queryParams.append('componentId', params.componentId.toString());

                return {
                    url: `${endpoints.componentContents.history}?${queryParams.toString()}`,
                    method: 'GET',
                };
            },
            providesTags: ['ComponentContent'],
        }),
        UpdateComponentContentDuration: builder.mutation<
            IResponse<ComponentContent.UpdateDurationRequest>,
            ComponentContent.UpdateDurationRequest
        >({
            query: (data) => ({
                url: `${endpoints.componentContents.base}/${data.id}/duration`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: ['ComponentContent'],
        }),

        // Publish component content
        PublishComponentContent: builder.mutation<
            IResponse<any>,
            { screenId: number; screenFrameConfigId: number; }
        >({
            query: (data) => ({
                url: endpoints.componentContents.publish,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ComponentContent'],
        }),
        GetApprovalPendings: builder.query<IApiResponse<ComponentContent.ApprovalPendings[]>, ComponentContent.ApprovalPendingFilter>({
            query: (filter) => {
                const params: any = {};
                if (filter.institutionId) params.InstitutionId = filter.institutionId;
                if (filter.page) params.Page = filter.page;
                if (filter.pageSize !== undefined) {
                    if (filter.pageSize === 0) {
                    } else {
                        params.Offset = filter.pageSize;
                        params.Skip = ((filter.page || 1) - 1) * filter.pageSize;
                    }
                }

                return {
                    url: endpoints.componentContents.approvalPendings,
                    method: "GET",
                    params: params,
                };
            },
            providesTags: ['ComponentContent'],
            keepUnusedDataFor: 0,
        }),
        // Approve or Reject component content
        ApproveComponentContent: builder.mutation<
            IResponse<any>,
            ComponentContent.ApprovalRequest
        >({
            query: (data) => ({
                url: endpoints.componentContents.approve,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: ['ComponentContent'],
        }),
    }),
});

export const {
    useCreateComponentContentMutation,
    useGetComponentContentByIdQuery,
    useGetComponentContentByComponentIdQuery,
    useUpdateComponentContentMutation,
    useDeleteComponentContentMutation,
    useGetComponentContentsQuery,
    useGetComponentContentHistoryQuery,
    useComponentContentOrderMutation,
    useUpdateComponentContentDurationMutation,
    usePublishComponentContentMutation,
    useGetApprovalPendingsQuery,
    useApproveComponentContentMutation
} = componentContentApi;
